import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import TopBar from "@/components/TopBar";
import IntegrationsCard from "@/components/IntegrationsCard";
import { Auth, Integrations } from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import { useUIStore, type Theme } from "@/store/ui";
import { Segmented } from "@/components/ui";

export default function SettingsPage() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [banner, setBanner] = useState<{ tone: "ok" | "error"; text: string } | null>(null);

  // After the Google OAuth round-trip the backend redirects to
  // /settings?google=connected|error. Read it once, surface a banner,
  // refresh the integrations list, then strip the param from the URL.
  useEffect(() => {
    const google = searchParams.get("google");
    if (!google) return;
    if (google === "connected") {
      setBanner({ tone: "ok", text: "Google connected." });
      qc.invalidateQueries({ queryKey: ["integrations"] });
    } else if (google === "error") {
      setBanner({ tone: "error", text: "Could not connect Google. Please try again." });
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, qc]);

  return (
    <>
      <TopBar title="Settings" />
      {banner && (
        <div className="px-4 md:px-6 pt-4">
          <div
            className={`panel-tight px-4 py-2.5 text-sm ${
              banner.tone === "ok" ? "text-accent-azure" : "text-accent-pink"
            }`}
          >
            {banner.text}
          </div>
        </div>
      )}

      <div className="px-4 py-4 md:px-6 md:py-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AppearanceCard />
        <PasswordCard />
        <IntegrationsSection />
      </div>
    </>
  );
}

// ─── Appearance ───────────────────────────────────────────────

function AppearanceCard() {
  const { theme, setTheme } = useUIStore();
  return (
    <section className="panel p-5">
      <h2 className="section-title mb-4">Appearance</h2>
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div>
          <div className="text-md font-medium">Theme</div>
          <div className="text-sm text-mut-500">
            System follows your computer's light or dark setting. Saved on this device.
          </div>
        </div>
        <Segmented<Theme>
          label="Theme"
          value={theme}
          onChange={setTheme}
          options={[
            { value: "light", label: "Light" },
            { value: "dark", label: "Dark" },
            { value: "system", label: "System" },
          ]}
        />
      </div>
    </section>
  );
}

// ─── Integrations ─────────────────────────────────────────────

function IntegrationsSection() {
  const integrationsQuery = useQuery({
    queryKey: ["integrations"],
    queryFn: () => Integrations.list(),
  });
  return (
    <section className="panel p-5">
      <h2 className="section-title mb-4">Integrations</h2>
      <div className="space-y-2">
        {(integrationsQuery.data ?? []).map((p) => (
          <IntegrationsCard key={p.provider} provider={p} />
        ))}
        {integrationsQuery.data?.length === 0 && (
          <p className="text-xs text-mut-500">No integrations available.</p>
        )}
      </div>
    </section>
  );
}

// ─── Password ─────────────────────────────────────────────────

function PasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () => Auth.changePassword(current, next),
    onSuccess: () => {
      setMsg("Password updated.");
      setCurrent("");
      setNext("");
    },
    onError: (e) => setMsg(apiErrorMessage(e)),
  });
  return (
    <section className="panel p-5">
      <h2 className="section-title mb-4">Your account</h2>
      <div className="space-y-3">
        <div>
          <label className="label">Current password</label>
          <input
            type="password"
            className="input"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </div>
        <div>
          <label className="label">New password</label>
          <input
            type="password"
            className="input"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </div>
        <button
          className="btn btn-primary"
          disabled={!current || !next || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          Change password
        </button>
        {msg && <div className="text-sm text-mut-300">{msg}</div>}
      </div>
    </section>
  );
}
